import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Trash2, CheckCircle, Clock, AlertCircle, Lightbulb, HelpCircle, Copy, ExternalLink, CheckCircle2, MessageSquare, Upload, X, FileImage, FileVideo, Send, XCircle, ZoomIn, Bell, Plus } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORRECTION_TYPES = {
  error: { label: "Error", icon: AlertCircle, class: "type-error" },
  mejora: { label: "Mejora", icon: Lightbulb, class: "type-mejora" },
  omision: { label: "Omisión", icon: HelpCircle, class: "type-omision" },
  otro: { label: "Otro", icon: HelpCircle, class: "type-otro" },
};

const PRIORITIES = [
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baja", label: "Baja" },
];

const STATUSES = [
  { value: "pendiente", label: "Pendiente" },
  { value: "retroalimentacion", label: "Retroalimentación" },
  { value: "completado", label: "Completado" },
];

// Format date to Mexico timezone
const formatDateMexico = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString("es-MX", {
    timeZone: "America/Mexico_City",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

// Handle paste for images
const handlePasteImages = (e, setFiles) => {
  const items = e.clipboardData?.items;
  if (!items) return;
  
  const imageFiles = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.startsWith('image/')) {
      const file = items[i].getAsFile();
      if (file) {
        if (file.size > 15 * 1024 * 1024) {
          toast.error("La imagen pegada excede 15MB");
          continue;
        }
        // Rename pasted image with timestamp
        const newFile = new File([file], `captura_${Date.now()}.png`, { type: file.type });
        imageFiles.push(newFile);
      }
    }
  }
  
  if (imageFiles.length > 0) {
    setFiles(prev => [...prev, ...imageFiles]);
    toast.success(`${imageFiles.length} imagen(es) pegada(s)`);
  }
};

export default function AdminCategoryDetail() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pendientes");
  
  // Chat dialog state
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatReply, setChatReply] = useState("");
  const [chatFiles, setChatFiles] = useState([]);
  const [sendingChat, setSendingChat] = useState(false);
  const [feedbackLink, setFeedbackLink] = useState("");
  const messagesEndRef = useRef(null);
  
  // Close case dialog state
  const [closeCaseDialogOpen, setCloseCaseDialogOpen] = useState(false);

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Reminders state
  const [reminders, setReminders] = useState([]);
  const [newReminder, setNewReminder] = useState("");
  const [addingReminder, setAddingReminder] = useState(false);

  const openImageViewer = (imageData, contentType, filename) => {
    setSelectedImage({ data: imageData, contentType, filename });
    setImageViewerOpen(true);
  };

  const token = localStorage.getItem("adminToken");

  const fetchData = useCallback(async () => {
    try {
      const [catRes, corrRes] = await Promise.all([
        axios.get(`${API}/categories/${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/corrections/${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCategory(catRes.data);
      setCorrections(corrRes.data);
      setReminders(catRes.data.reminders || []);
      
      await axios.post(`${API}/corrections/mark-all-viewed/${categoryId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/webo0os");
      }
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [categoryId, token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/webo0os");
      return;
    }
    fetchData();
  }, [token, navigate, fetchData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Sort by priority: alta > media > baja
  const priorityOrder = { alta: 0, media: 1, baja: 2 };
  const sortByPriority = (a, b) => (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);

  // Filter corrections by status and sort by priority
  const pendingCorrections = corrections.filter(c => c.status === "pendiente").sort(sortByPriority);
  const feedbackCorrections = corrections.filter(c => c.status === "retroalimentacion").sort(sortByPriority);
  const completedCorrections = corrections.filter(c => c.status === "completado").sort(sortByPriority);
  
  // Filter corrections that need response (client replied and admin hasn't seen)
  const needsResponseCorrections = corrections.filter(c => c.has_unread_client_reply === true).sort(sortByPriority);

  const updateCorrection = async (correctionId, data) => {
    try {
      await axios.put(`${API}/corrections/${correctionId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Corrección actualizada");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const deleteCorrection = async (correctionId) => {
    try {
      await axios.delete(`${API}/corrections/${correctionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Corrección eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Reminder functions
  const addReminder = async () => {
    if (!newReminder.trim()) return;
    setAddingReminder(true);
    try {
      await axios.post(`${API}/categories/${categoryId}/reminders`, 
        { instruction: newReminder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Recordatorio agregado");
      setNewReminder("");
      fetchData();
    } catch (error) {
      toast.error("Error al agregar recordatorio");
    } finally {
      setAddingReminder(false);
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}/reminders/${reminderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Recordatorio eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar recordatorio");
    }
  };

  const copyClientUrl = () => {
    navigator.clipboard.writeText(`${window.location.origin}/`);
    toast.success("URL copiada");
  };

  const copyPassword = () => {
    if (category) {
      navigator.clipboard.writeText(category.password);
      toast.success("Contraseña copiada");
    }
  };

  const getPriorityClass = (priority) => {
    const classes = { alta: "priority-alta", media: "priority-media", baja: "priority-baja" };
    return classes[priority] || "priority-media";
  };

  const getStatusClass = (status) => {
    if (status === "completado") return "status-completado";
    if (status === "retroalimentacion") return "bg-purple-900/30 text-purple-400 border border-purple-900/50";
    return "status-pendiente";
  };

  // Handle status change - open chat if selecting retroalimentacion
  const handleStatusChange = async (correction, newStatus) => {
    if (newStatus === "retroalimentacion") {
      // Open chat dialog
      setSelectedCorrection(correction);
      setChatMessages(correction.feedback_messages || []);
      setChatReply("");
      setChatFiles([]);
      setFeedbackLink(correction.feedback_token ? `${window.location.origin}/feedback/${correction.feedback_token}` : "");
      setChatDialogOpen(true);
      
      // Update status
      await updateCorrection(correction.id, { status: newStatus });
    } else {
      await updateCorrection(correction.id, { status: newStatus });
    }
  };

  // Open chat for existing feedback correction
  const openChat = async (correction) => {
    setSelectedCorrection(correction);
    setChatMessages(correction.feedback_messages || []);
    setChatReply("");
    setChatFiles([]);
    setFeedbackLink(correction.feedback_token ? `${window.location.origin}/feedback/${correction.feedback_token}` : "");
    setChatDialogOpen(true);
    
    // Mark as read if there are unread client replies
    if (correction.has_unread_client_reply) {
      try {
        await axios.post(`${API}/feedback/${correction.id}/mark-read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchData(); // Refresh to update the UI
      } catch (error) {
        console.error("Error marking as read:", error);
      }
    }
  };

  const handleChatFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => {
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name} excede 15MB`);
        return false;
      }
      return true;
    });
    setChatFiles(prev => [...prev, ...validFiles]);
  };

  const removeChatFile = (index) => {
    setChatFiles(prev => prev.filter((_, i) => i !== index));
  };

  const sendChatMessage = async () => {
    if (!chatReply.trim() && chatFiles.length === 0) {
      toast.error("Escribe un mensaje o adjunta un archivo");
      return;
    }

    setSendingChat(true);
    const formData = new FormData();
    formData.append("message", chatReply);
    chatFiles.forEach(file => formData.append("files", file));

    try {
      // If no feedback exists yet, start it. Otherwise reply.
      const endpoint = selectedCorrection.feedback_token 
        ? `${API}/feedback/${selectedCorrection.id}/reply`
        : `${API}/feedback/${selectedCorrection.id}/start`;
      
      const response = await axios.post(endpoint, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.feedback_url) {
        const newLink = `${window.location.origin}${response.data.feedback_url}`;
        setFeedbackLink(newLink);
        toast.success("Mensaje enviado. Link generado para el cliente.");
      } else {
        toast.success("Mensaje enviado");
      }
      
      setChatReply("");
      setChatFiles([]);
      fetchData();
      
      // Refresh messages
      const corrRes = await axios.get(`${API}/admin/corrections/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = corrRes.data.find(c => c.id === selectedCorrection.id);
      if (updated) {
        setChatMessages(updated.feedback_messages || []);
        setSelectedCorrection(updated);
      }
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingChat(false);
    }
  };

  const closeFeedbackCase = async (newStatus) => {
    try {
      // First close the feedback, then update to selected status
      await axios.post(`${API}/feedback/${selectedCorrection.id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update to selected status
      await axios.put(`${API}/corrections/${selectedCorrection.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === "completado" ? "Caso completado" : "Caso movido a pendientes");
      setCloseCaseDialogOpen(false);
      setChatDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al cerrar caso");
    }
  };

  const renderCorrectionCard = (correction, index, tab) => {
    const typeInfo = CORRECTION_TYPES[correction.correction_type] || CORRECTION_TYPES.otro;
    const TypeIcon = typeInfo.icon;
    const isCompleted = tab === "completadas";
    const isFeedback = tab === "retroalimentacion";
    
    return (
      <Card 
        key={correction.id} 
        className={`bg-card border-border card-hover animate-fade-in ${isCompleted ? 'border-green-800/30' : ''} ${isFeedback ? 'border-purple-800/30' : ''}`}
        style={{ animationDelay: `${index * 0.03}s` }}
        data-testid={`correction-item-${correction.id}`}
      >
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-3 mb-4">
                <div className={`p-2 rounded-sm ${isCompleted ? 'bg-green-900/30' : isFeedback ? 'bg-purple-900/30' : typeInfo.class}`}>
                  {isCompleted ? <CheckCircle2 className="w-4 h-4 text-green-400" /> 
                   : isFeedback ? <MessageSquare className="w-4 h-4 text-purple-400" />
                   : <TypeIcon className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono bg-primary/20 text-primary px-2 py-0.5 rounded">
                      ID: {String(correction.sequential_id || 0).padStart(2, '0')}
                    </span>
                    <h3 className={`font-semibold text-lg ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                      {correction.title}
                    </h3>
                    {correction.has_unread_client_reply && (
                      <span className="inline-flex items-center justify-center w-3 h-3 bg-red-500 rounded-full animate-pulse" title="Nueva respuesta del cliente"></span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge className={`text-xs uppercase ${getPriorityClass(correction.priority)}`}>
                      {correction.priority}
                    </Badge>
                    <Badge className={`text-xs uppercase ${getStatusClass(correction.status)} flex items-center gap-1`}>
                      {isFeedback ? <><MessageSquare className="w-3 h-3" /> Retroalimentación</>
                       : isCompleted ? <><CheckCircle className="w-3 h-3" /> Completado</>
                       : <><Clock className="w-3 h-3" /> Pendiente</>}
                    </Badge>
                    {correction.has_unread_client_reply && (
                      <Badge className="text-xs bg-red-900/50 text-red-400 border border-red-500 animate-pulse">
                        ¡NUEVA RESPUESTA!
                      </Badge>
                    )}
                    {correction.feedback_messages?.length > 0 && (
                      <Badge className="text-xs bg-blue-900/30 text-blue-400 border border-blue-900/50">
                        {correction.feedback_messages.length} mensaje(s)
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground mono">
                      {formatDateMexico(correction.created_at)}
                    </span>
                  </div>
                  <p className={`text-sm whitespace-pre-wrap ${isCompleted ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                    {correction.description}
                  </p>
                </div>
              </div>

              {correction.attachments?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {correction.attachments.map((att, i) => (
                    <div key={att.id || i}>
                      {att.content_type?.startsWith("video") ? (
                        <video src={`data:${att.content_type};base64,${att.data}`} className="w-32 h-32 object-cover rounded-sm border border-border" controls />
                      ) : (
                        <div 
                          className="relative group cursor-pointer"
                          onClick={() => openImageViewer(att.data, att.content_type, att.filename)}
                        >
                          <img src={`data:${att.content_type};base64,${att.data}`} alt={att.filename}
                            className="w-32 h-32 object-cover rounded-sm border border-border" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm">
                            <ZoomIn className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 lg:min-w-[200px]">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Prioridad</p>
                <Select value={correction.priority} onValueChange={(v) => updateCorrection(correction.id, { priority: v })}>
                  <SelectTrigger className="bg-input border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Estado</p>
                <Select value={correction.status} onValueChange={(v) => handleStatusChange(correction, v)}>
                  <SelectTrigger className="bg-input border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Botón VER CHAT - visible siempre que haya mensajes o sea retroalimentación */}
              {(correction.feedback_messages?.length > 0 || isFeedback) && (
                <Button 
                  onClick={() => openChat(correction)}
                  className={`uppercase tracking-wider ${correction.has_unread_client_reply ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                  data-testid={`chat-button-${correction.id}`}
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> 
                  {correction.has_unread_client_reply ? '¡Ver Respuesta!' : `Ver Chat (${correction.feedback_messages?.length || 0})`}
                </Button>
              )}

              {correction.feedback_token && (
                <Button variant="outline" size="sm" className="border-border"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/feedback/${correction.feedback_token}`); toast.success("Link copiado"); }}>
                  <Copy className="w-4 h-4 mr-2" /> Copiar Link
                </Button>
              )}

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-border text-destructive hover:bg-destructive/10 mt-2">
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar corrección?</AlertDialogTitle>
                    <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteCorrection(correction.id)} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-muted-foreground">Cargando...</div></div>;
  if (!category) return <div className="min-h-screen flex items-center justify-center"><div className="text-muted-foreground">Categoría no encontrada</div></div>;

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="admin-category-detail">
      <header className="mb-8">
        <Button variant="ghost" onClick={() => navigate("/webo0os/dashboard")} className="mb-6 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Dashboard
        </Button>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-foreground mb-2">{category.name}</h1>
            <p className="text-muted-foreground text-sm mb-4">{category.description || "Sin descripción"}</p>
            <p className="text-xs text-muted-foreground mono">{corrections.length} CORRECCIONES EN TOTAL</p>
          </div>

          <Card className="bg-card border-border lg:min-w-[300px]">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm uppercase tracking-wider text-muted-foreground font-semibold">Acceso para Cliente</p>
              <div>
                <p className="text-xs text-muted-foreground mb-1">URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-2 py-1 rounded-sm text-sm mono truncate">{window.location.origin}/</code>
                  <Button size="sm" variant="outline" onClick={copyClientUrl}><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => window.open("/", "_blank")}><ExternalLink className="w-3 h-3" /></Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Contraseña</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-2 py-1 rounded-sm text-sm mono">{category.password}</code>
                  <Button size="sm" variant="outline" onClick={copyPassword}><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </header>

      {/* Reminders Section for Admin */}
      <Card className="bg-yellow-900/20 border-yellow-600/50 mb-8">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-semibold uppercase tracking-wider text-yellow-400">Recordatorios</h3>
            <Badge className="bg-yellow-600 text-white">{reminders.length}</Badge>
          </div>
          
          {/* Add new reminder */}
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Escribir nueva instrucción..."
              value={newReminder}
              onChange={(e) => setNewReminder(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addReminder()}
              className="bg-input border-yellow-600/50 focus:border-yellow-500"
            />
            <Button 
              onClick={addReminder}
              disabled={addingReminder || !newReminder.trim()}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>

          {/* List reminders */}
          {reminders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay recordatorios. Los recordatorios que agregues aquí serán visibles para el cliente.
            </p>
          ) : (
            <div className="space-y-2">
              {reminders.map((reminder) => (
                <div key={reminder.id} className="flex items-center justify-between bg-yellow-900/30 border border-yellow-600/30 rounded-sm p-3">
                  <p className="text-sm text-yellow-100">{reminder.instruction}</p>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => deleteReminder(reminder.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs: Necesitan Respuesta, Pendientes, Retroalimentación, Completadas */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-4xl grid-cols-4 mb-8 bg-secondary/50">
          <TabsTrigger value="necesitan-respuesta" className={`data-[state=active]:bg-red-900/50 data-[state=active]:text-red-400 uppercase tracking-wider text-xs ${needsResponseCorrections.length > 0 ? 'animate-pulse' : ''}`}>
            <AlertCircle className="w-4 h-4 mr-1" /> Necesitan Respuesta
            <Badge className={`ml-1 text-xs ${needsResponseCorrections.length > 0 ? 'bg-red-600 text-white border-red-500' : 'bg-red-900/50 text-red-400 border-red-900'}`}>{needsResponseCorrections.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="pendientes" className="data-[state=active]:bg-orange-900/50 data-[state=active]:text-orange-400 uppercase tracking-wider text-xs">
            <Clock className="w-4 h-4 mr-1" /> Pendientes
            <Badge className="ml-1 bg-orange-900/50 text-orange-400 border-orange-900 text-xs">{pendingCorrections.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="retroalimentacion" className="data-[state=active]:bg-purple-900/50 data-[state=active]:text-purple-400 uppercase tracking-wider text-xs">
            <MessageSquare className="w-4 h-4 mr-1" /> Retroalimentación
            <Badge className="ml-1 bg-purple-900/50 text-purple-400 border-purple-900 text-xs">{feedbackCorrections.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="completadas" className="data-[state=active]:bg-green-900/50 data-[state=active]:text-green-400 uppercase tracking-wider text-xs">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Completadas
            <Badge className="ml-1 bg-green-900/50 text-green-400 border-green-900 text-xs">{completedCorrections.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Necesitan Respuesta */}
        <TabsContent value="necesitan-respuesta">
          {needsResponseCorrections.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">¡Todo al día!</h3>
              <p className="text-muted-foreground text-sm">No hay respuestas de clientes pendientes de revisar</p>
            </div>
          ) : (
            <div className="space-y-4">{needsResponseCorrections.map((c, i) => renderCorrectionCard(c, i, "necesitan-respuesta"))}</div>
          )}
        </TabsContent>

        <TabsContent value="pendientes">
          {pendingCorrections.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">¡Sin tareas pendientes!</h3>
            </div>
          ) : (
            <div className="space-y-4">{pendingCorrections.map((c, i) => renderCorrectionCard(c, i, "pendientes"))}</div>
          )}
        </TabsContent>

        <TabsContent value="retroalimentacion">
          {feedbackCorrections.length === 0 ? (
            <div className="text-center py-20">
              <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sin retroalimentación activa</h3>
              <p className="text-muted-foreground text-sm">Cambia el estado de una corrección a "Retroalimentación" para iniciar</p>
            </div>
          ) : (
            <div className="space-y-4">{feedbackCorrections.map((c, i) => renderCorrectionCard(c, i, "retroalimentacion"))}</div>
          )}
        </TabsContent>

        <TabsContent value="completadas">
          {completedCorrections.length === 0 ? (
            <div className="text-center py-20">
              <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sin correcciones completadas</h3>
            </div>
          ) : (
            <div className="space-y-4">{completedCorrections.map((c, i) => renderCorrectionCard(c, i, "completadas"))}</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Chat Dialog */}
      <Dialog open={chatDialogOpen} onOpenChange={setChatDialogOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl uppercase tracking-wider flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Retroalimentación
            </DialogTitle>
          </DialogHeader>
          
          {selectedCorrection && (
            <div className="flex-1 flex flex-col overflow-hidden mt-4">
              {/* Botón CERRAR CASO más llamativo y separado */}
              <div className="flex items-center justify-between bg-muted/30 rounded-sm p-3 mb-4 flex-shrink-0">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Corrección</p>
                  <p className="font-semibold text-sm">{selectedCorrection.title}</p>
                </div>
                <Button 
                  onClick={() => setCloseCaseDialogOpen(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 uppercase tracking-wider font-bold"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Cerrar Caso
                </Button>
              </div>

              {feedbackLink && (
                <div className="bg-purple-900/20 border border-purple-900/50 rounded-sm p-3 mb-4 flex-shrink-0">
                  <p className="text-xs uppercase tracking-wider text-purple-400 mb-2">Link para el cliente</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-muted px-2 py-1 rounded-sm text-xs mono truncate">{feedbackLink}</code>
                    <Button size="sm" onClick={() => { navigator.clipboard.writeText(feedbackLink); toast.success("Link copiado"); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Escribe tu primera duda o comentario</p>
                  </div>
                ) : (
                  chatMessages.map((msg, index) => (
                    <div key={msg.id || index} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] ${msg.sender === "admin" ? "bg-primary/20 border-r-2 border-r-primary" : "bg-secondary/50 border-l-2 border-l-green-500"} rounded-md p-3`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold uppercase">{msg.sender === "admin" ? "Tú" : "Cliente"}</span>
                          <span className="text-xs text-muted-foreground mono">{formatDateMexico(msg.created_at)}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        {msg.attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {msg.attachments.map((att, i) => (
                              <div 
                                key={i}
                                className="relative group cursor-pointer"
                                onClick={() => openImageViewer(att.data, att.content_type, att.filename)}
                              >
                                <img src={`data:${att.content_type};base64,${att.data}`} alt={att.filename}
                                  className="w-20 h-20 object-cover rounded-sm border border-border" />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm">
                                  <ZoomIn className="w-6 h-6 text-white" />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="flex-shrink-0 border-t border-border pt-4">
                <Textarea 
                  value={chatReply} 
                  onChange={(e) => setChatReply(e.target.value)} 
                  onPaste={(e) => handlePasteImages(e, setChatFiles)}
                  placeholder="Escribe tu mensaje... (Ctrl+V para pegar imágenes)" 
                  className="bg-input border-border min-h-[80px] mb-3" 
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("chat-file-input").click()} className="border-border">
                      <Upload className="w-4 h-4 mr-2" /> Adjuntar
                    </Button>
                    <span className="text-xs text-muted-foreground hidden sm:inline">o Ctrl+V</span>
                    <input id="chat-file-input" type="file" multiple accept="image/*,video/*" onChange={handleChatFileChange} className="hidden" />
                    {chatFiles.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {chatFiles.map((file, i) => (
                          <div key={i} className="flex items-center gap-1 bg-muted px-2 py-1 rounded-sm text-xs">
                            {file.type.startsWith("video") ? <FileVideo className="w-3 h-3" /> : <FileImage className="w-3 h-3" />}
                            <span className="max-w-[60px] truncate">{file.name}</span>
                            <button onClick={() => removeChatFile(i)}><X className="w-3 h-3 hover:text-destructive" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={sendChatMessage} disabled={sendingChat} className="bg-primary hover:bg-primary/90">
                    <Send className="w-4 h-4 mr-2" /> {sendingChat ? "Enviando..." : "Enviar"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo para Cerrar Caso con opciones */}
      <AlertDialog open={closeCaseDialogOpen} onOpenChange={setCloseCaseDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">¿A dónde mover esta corrección?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Selecciona el estado final después de cerrar el caso de retroalimentación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button 
              onClick={() => closeFeedbackCase("completado")}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg uppercase tracking-wider"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" /> Completado
            </Button>
            <Button 
              onClick={() => closeFeedbackCase("pendiente")}
              variant="outline"
              className="w-full border-orange-600 text-orange-400 hover:bg-orange-900/20 py-6 text-lg uppercase tracking-wider"
            >
              <Clock className="w-5 h-5 mr-2" /> Pendiente
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Viewer Dialog */}
      <Dialog open={imageViewerOpen} onOpenChange={setImageViewerOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] p-2">
          <DialogHeader className="p-2">
            <DialogTitle className="text-sm text-muted-foreground">
              {selectedImage?.filename || "Imagen"}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="flex items-center justify-center overflow-auto max-h-[80vh]">
              <img 
                src={`data:${selectedImage.contentType};base64,${selectedImage.data}`}
                alt={selectedImage.filename}
                className="max-w-full max-h-[75vh] object-contain rounded-sm"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
